// Catches render/lifecycle exceptions so a thrown error shows a recoverable panel instead of a
// blank white screen (React unmounts the whole tree on an uncaught error). Note: errors thrown
// directly inside DOM event handlers don't reach here — the window listeners below surface those.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { messages as en } from "../locales/en/messages";
import { open as openDialog } from "@tauri-apps/plugin-dialog";
import { openUrl } from "@tauri-apps/plugin-opener";
import { isRemote } from "../lib/rpc";
import { exportDebugLog, logError } from "../lib/debugLog";
import { missingDataRoot, pathExists, relocateDataDir } from "../lib/api";
import { activeAccountId, getAccount, relocateCandidates } from "../lib/accounts";

/**
 * Is this error someone else's code? The crash panel is for OUR bugs, but window "error" /
 * "unhandledrejection" also fire for browser and extension content scripts running in the page —
 * a remote user's Orion (WebKit) browser crashed its own injected script ("undefined is not an
 * object (evaluating 'this.domQueryService')", a name that appears nowhere in this app) and the
 * panel blamed us for it. Every error OUR code can throw here carries an app-origin script URL
 * (the bundle) in the event's filename or the stack — nothing in the app is eval'd — so: if the
 * error names any script source at all and none of it is ours, it's foreign. No sources named
 * (bare "Script error.", stackless custom errors) stays OURS — when in doubt, show the panel.
 */
export function isForeignScriptError(origin: string, filename: string | undefined, stack: string | undefined): boolean {
  const sources = [filename ?? "", ...(stack ?? "").split("\n")]
    .flatMap((s) => s.match(/[a-z][a-z0-9+.-]*:\/\/[^\s()]+/gi) ?? []);
  return sources.length > 0 && !sources.some((u) => u.startsWith(origin));
}

/** This module loads before main.tsx activates the UI language, and the panel can show for a crash that
 *  happened before activation finished — where Lingui's t`…` throws ("without setting a locale") and would
 *  take the crash panel down with it. Activate English (the source language, already bundled) if nothing
 *  is active yet; a no-op once the real language is up. Call before any t`…` here. */
function ensureLocale(): void {
  if (!i18n.locale) i18n.loadAndActivate({ locale: "en", messages: en });
}

type Props = { children: ReactNode };
type State = {
  error: Error | null;
  stack: string;
  exportMsg: string;
  lostRoot: string | null; // set when the crash is "the data folder is gone" — enables the relocate flow
  relocateMsg: string;
};

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: "", exportMsg: "", lostRoot: null, relocateMsg: "" };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface where it happened (component stack pinpoints the file/component that threw).
    const stack = `${error.stack ?? ""}\n\nComponent stack:${info.componentStack ?? ""}`;
    this.setState({ stack });
    this.checkDataRoot();
    logError("render crash", stack);
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  exportLog = () => {
    ensureLocale();
    this.setState({ exportMsg: t`Saving…` });
    void exportDebugLog()
      .then((path) => this.setState({ exportMsg: t`Saved to ${path}` }))
      .catch((e) => { const err = String(e); this.setState({ exportMsg: t`Export failed: ${err}` }); });
  };

  /** Every error routes through here, so ask once whether the data root still exists. Cheap (one stat) and
   *  it turns the most common unrecoverable-looking crash into a fixable one. Failure to check is ignored —
   *  the plain crash panel is the fallback. */
  checkDataRoot = () => {
    void missingDataRoot()
      .then((root) => { if (root) this.setState({ lostRoot: root }); })
      .catch(() => {});
  };

  /** Point this account at its vault's NEW location. Only ever rewrites the pointer + registry — the data
   *  is already at the new path, so moving anything would be wrong (and `changeDataDir` would try). */
  relocate = async () => {
    ensureLocale();
    this.setState({ relocateMsg: "" });
    try {
      const picked = await openDialog({ directory: true, multiple: false, title: t`Find your Rin Chat data folder` });
      if (typeof picked !== "string") return; // cancelled
      const entry = await getAccount(activeAccountId());
      if (!entry) { this.setState({ relocateMsg: t`No account is active — reload and try again.` }); return; }
      this.setState({ relocateMsg: t`Checking…` });
      for (const c of relocateCandidates(picked, entry.username)) {
        if (!(await pathExists(`${c.root}/aicc.db`))) continue;
        await relocateDataDir(c.root, c.base);
        const { root } = c;
        this.setState({ relocateMsg: t`Found your data at ${root} — reloading…` });
        setTimeout(() => window.location.reload(), 700);
        return;
      }
      this.setState({ relocateMsg: t`No Rin Chat data (aicc.db) in that folder. Pick the folder holding your data, or the drive it lives on.` });
    } catch (e) {
      const err = String(e);
      this.setState({ relocateMsg: t`Couldn't use that folder: ${err}` });
    }
  };

  componentDidMount() {
    // Uncaught errors + rejected promises from event handlers never hit getDerivedStateFromError,
    // so trap them here too and show the same panel rather than letting the app sit half-broken.
    window.addEventListener("error", this.onWindowError);
    window.addEventListener("unhandledrejection", this.onRejection);
  }
  componentWillUnmount() {
    window.removeEventListener("error", this.onWindowError);
    window.removeEventListener("unhandledrejection", this.onRejection);
  }
  onWindowError = (e: ErrorEvent) => {
    if (!(e.error instanceof Error)) return;
    if (isForeignScriptError(window.location.origin, e.filename, e.error.stack)) {
      logError("foreign script error (ignored)", `${e.filename || "?"}: ${e.error.message}`);
      return;
    }
    logError("window error", e.error.stack ?? e.error.message); this.setState({ error: e.error }); this.checkDataRoot();
  };
  onRejection = (e: PromiseRejectionEvent) => {
    const r = e.reason;
    const err = r instanceof Error ? r : new Error(String(r));
    // Aborted requests (Stop button, switching/deleting a chat mid-stream) reject normally — don't turn
    // those into a full-screen crash.
    if (err.name === "AbortError") return;
    // Same for a remote client losing authorisation: the PIN window lapses, the owner revokes the device,
    // the desktop signs out. Every in-flight call then rejects with 401 at once. That is a session state —
    // lib/rpc announces it and the app shows the pairing/PIN gate — not something to crash the UI over.
    if (err.name === "RemoteRpcError" && (err as { status?: number }).status === 401) return;
    // 409 = another device holds the write lease. The app offers to take over; crashing would be a
    // spectacularly unhelpful way to say "someone else is typing".
    if (err.name === "RemoteRpcError" && (err as { status?: number }).status === 409) return;
    // Same foreign-script guard as onWindowError — an extension's rejected promise lands here too.
    if (r instanceof Error && isForeignScriptError(window.location.origin, undefined, r.stack)) {
      logError("foreign script rejection (ignored)", r.message);
      return;
    }
    logError("unhandled rejection", err.stack ?? err.message);
    this.setState({ error: err });
    this.checkDataRoot();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    ensureLocale();
    const { lostRoot } = this.state;
    return (
      <div className="crash-screen">
        <div className="crash-card">
          <h2>{lostRoot ? t`Can't find your data folder` : t`Something went wrong`}</h2>
          <p className="status">
            {lostRoot
              ? t`Your data hasn't been deleted — Rin Chat just can't reach the folder it was told to use. This happens when a drive letter changes or the folder is moved or renamed. Point it at the new location to continue.`
              : t`The last action hit an error. Your data is safe — reload to continue.`}
          </p>
          {lostRoot && <pre className="crash-detail">{t`Expected at: ${lostRoot}`}</pre>}
          <pre className="crash-detail">{error.message || String(error)}</pre>
          {this.state.stack && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ cursor: "pointer", color: "var(--muted)" }}>{t`Technical details`}</summary>
              <pre className="crash-detail" style={{ maxHeight: 240, overflow: "auto" }}>{this.state.stack}</pre>
            </details>
          )}
          <div className="crash-actions">
            {/* Repointing an account at its vault is a desktop job — the folder is on the desktop's disk
                and a remote browser can't name it. Remotely the message alone is the whole answer. */}
            {this.state.lostRoot && !isRemote() && <button className="btn btn-cta" onClick={() => void this.relocate()}>{t`Locate data folder`}</button>}
            <button className={this.state.lostRoot ? "btn" : "btn btn-cta"} onClick={() => window.location.reload()}>{t`Reload`}</button>
            <button className="btn" onClick={() => this.setState({ error: null, stack: "", exportMsg: "", lostRoot: null, relocateMsg: "" })}>{t`Dismiss`}</button>
            <button className="btn" onClick={this.exportLog}>{t`Export debug log`}</button>
          </div>
          {/* A button, not an anchor: App's link interceptor is unmounted while this panel shows, so a
              plain href navigated the webview away — taking Reload and Export with it. */}
          <p className="status" style={{ marginTop: 10 }}>
            <Trans>
              Stuck? Report it on the community Discord:{" "}
              <button style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "var(--accent)", textDecoration: "underline", cursor: "pointer" }} onClick={() => { const u = "https://discord.rin.chat"; if (isRemote()) window.open(u, "_blank", "noopener"); else void openUrl(u).catch(() => {}); }}>discord.rin.chat</button>
. Attach the exported debug log if you can, or send it to the developer in a Discord DM. API keys and sign-in tokens are taken out of it, but it does hold your recent chats as plain text, so only share it if you're comfortable.
            </Trans>
          </p>
          {this.state.relocateMsg && <p className="status" style={{ marginTop: 10, wordBreak: "break-all" }}>{this.state.relocateMsg}</p>}
          {this.state.exportMsg && <p className="status" style={{ marginTop: 10, wordBreak: "break-all" }}>{this.state.exportMsg}</p>}
        </div>
      </div>
    );
  }
}
